import type { LinkRegion } from '@roll1/shared';

/** Check if a point is inside a rect region (inclusive boundaries) */
export function pointInRect(wx: number, wy: number, r: { x: number; y: number; width: number; height: number }): boolean {
  return wx >= r.x && wx <= r.x + r.width && wy >= r.y && wy <= r.y + r.height;
}

/** Check if a point is inside a circle region (inclusive boundary) */
export function pointInCircle(wx: number, wy: number, c: { cx: number; cy: number; radius: number }): boolean {
  const dx = wx - c.cx;
  const dy = wy - c.cy;
  return dx * dx + dy * dy <= c.radius * c.radius;
}

/** Check if a point is inside a polygon region using ray-casting algorithm */
export function pointInPolygon(wx: number, wy: number, p: { points: Array<{ x: number; y: number }> }): boolean {
  const pts = p.points;
  if (pts.length < 3) return false;
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const xi = pts[i].x, yi = pts[i].y;
    const xj = pts[j].x, yj = pts[j].y;
    if ((yi > wy) !== (yj > wy) && wx < ((xj - xi) * (wy - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

/** Dispatch hit-test to the appropriate region type */
export function hitTestRegion(wx: number, wy: number, region: LinkRegion): boolean {
  switch (region.type) {
    case 'rect': return pointInRect(wx, wy, region);
    case 'circle': return pointInCircle(wx, wy, region);
    case 'polygon': return pointInPolygon(wx, wy, region);
  }
}

/** Get the center point of a region */
export function getRegionCenter(region: LinkRegion): { x: number; y: number } {
  switch (region.type) {
    case 'rect':
      return { x: region.x + region.width / 2, y: region.y + region.height / 2 };
    case 'circle':
      return { x: region.cx, y: region.cy };
    case 'polygon': {
      const pts = region.points;
      if (pts.length === 0) return { x: 0, y: 0 };
      const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
      const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
      return { x: cx, y: cy };
    }
    default:
      return { x: 0, y: 0 };
  }
}

/** Get the width (or diameter) of a region */
export function getRegionWidth(region: LinkRegion): number {
  switch (region.type) {
    case 'rect': return region.width;
    case 'circle': return region.radius * 2;
    case 'polygon': {
      const xs = region.points.map(p => p.x);
      return Math.max(...xs) - Math.min(...xs);
    }
    default:
      return 0;
  }
}

/** Truncate text with ellipsis to fit within maxWidth */
export function truncateLabel(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (maxWidth <= 0) return '';
  if (ctx.measureText(text).width <= maxWidth) return text;
  let truncated = text;
  while (truncated.length > 1 && ctx.measureText(truncated + '\u2026').width > maxWidth) {
    truncated = truncated.slice(0, -1);
  }
  return truncated + '\u2026';
}
